import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi, onAuthError, onTosRequired } from '../api';
import type { SSOProvider, PendingEmailResponse } from '../api/auth';
import { requiresEmail } from '../api/auth';
import type { User, LoginCredentials, RegisterCredentials } from '../types';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

// Result of SSO callback - either authenticated or needs email
export type SSOCallbackResult =
  | { type: 'authenticated' }
  | { type: 'pending_email'; pendingEmail: PendingEmailResponse };

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  // SSO methods
  getSSOProviders: () => Promise<SSOProvider[]>;
  loginWithSSO: (provider: string, redirectTo?: string) => Promise<void>;
  handleSSOCallback: (provider: string, code: string, state?: string) => Promise<SSOCallbackResult>;
  completeSSORegistration: (pendingEmailToken: string, email: string, tosAcceptedAt: string) => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
  });

  // Listen for auth errors (401 responses) and handle by logging out and redirecting
  useEffect(() => {
    const unsubscribe = onAuthError(() => {
      // Clear auth state - logout is now async
      authApi.logout().catch(() => {
        // Ignore logout errors
      });
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });

      // Skip redirect if already on login page or public pages
      const publicPaths = ['/', '/login', '/register', '/auth/callback', '/forgot-password', '/reset-password', '/verify-email', '/b/', '/support', '/blog', '/r/', '/terms', '/privacy'];
      const isPublicPath = publicPaths.some(path =>
        path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
      );

      if (!isPublicPath) {
        const redirectTo = location.pathname + location.search;
        navigate(`/login?redirectTo=${encodeURIComponent(redirectTo)}`, { replace: true });
      }
    });

    return unsubscribe;
  }, [navigate, location.pathname, location.search]);

  // Listen for ToS required errors (403 TOS_REQUIRED) and refresh user to show ToS modal
  // This handles cases where the user bypassed the modal (e.g., via DOM manipulation)
  useEffect(() => {
    const unsubscribe = onTosRequired(() => {
      // Refresh user data to ensure tosAcceptedAt is null in state
      // This will trigger the TosAcceptanceModal in Layout
      authApi.me().then((user) => {
        setState((prev) => ({
          ...prev,
          user,
        }));
      }).catch((error) => {
        // Log for debugging - the original API request already showed the error to user
        console.error('Failed to refresh user data after ToS required error:', error);
      });
    });

    return unsubscribe;
  }, []);

  // Check authentication on mount by calling /auth/me
  // With HTTP-only cookies, we can't check localStorage - must ask the server
  const checkAuth = useCallback(async () => {
    try {
      const user = await authApi.me();
      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch {
      // 401 or other error - not authenticated
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
        error: null,
      });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.login(credentials);
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw err;
    }
  }, []);

  const register = useCallback(async (credentials: RegisterCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.register(credentials);
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setState({
      user: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
    });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const user = await authApi.me();
      setState((prev) => ({
        ...prev,
        user,
      }));
    } catch {
      // Ignore errors on refresh
    }
  }, []);

  // SSO methods
  const getSSOProviders = useCallback(async (): Promise<SSOProvider[]> => {
    const response = await authApi.getSSOProviders();
    return response.providers;
  }, []);

  const loginWithSSO = useCallback(async (provider: string, redirectTo?: string) => {
    // Generate random state for CSRF protection
    const state = crypto.randomUUID();

    // Store state and provider in session storage for callback validation
    sessionStorage.setItem('sso_state', state);
    sessionStorage.setItem('sso_provider', provider);
    if (redirectTo) {
      sessionStorage.setItem('sso_redirect', redirectTo);
    }

    // Get the authorization URL from the backend
    const response = await authApi.getSSOAuthURL(provider, state);

    // Redirect to the OAuth provider
    window.location.href = response.authUrl;
  }, []);

  const handleSSOCallback = useCallback(async (provider: string, code: string, state?: string): Promise<SSOCallbackResult> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.handleSSOCallback(provider, code, state);

      // Check if this is a pending email response (Facebook user without email)
      if (requiresEmail(response)) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
        return { type: 'pending_email', pendingEmail: response };
      }

      // Normal authentication response
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
      return { type: 'authenticated' };
    } catch (err) {
      // Clean up session storage on failure so user can retry
      sessionStorage.removeItem('sso_state');
      sessionStorage.removeItem('sso_provider');
      sessionStorage.removeItem('sso_redirect');
      const message = err instanceof Error ? err.message : 'SSO authentication failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw err;
    }
  }, []);

  const completeSSORegistration = useCallback(async (pendingEmailToken: string, email: string, tosAcceptedAt: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.completeSSORegistration({
        pendingEmailToken,
        email,
        tosAcceptedAt,
      });
      setState({
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to complete registration';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      throw err;
    }
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      register,
      logout,
      refreshUser,
      getSSOProviders,
      loginWithSSO,
      handleSSOCallback,
      completeSSORegistration,
    }),
    [state, login, register, logout, refreshUser, getSSOProviders, loginWithSSO, handleSSOCallback, completeSSORegistration]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
